import { Box, Typography } from "@mui/material";
import { PayloadAction } from "@reduxjs/toolkit";
import { useStores } from "api/stores";
import { useAppDispatch } from "app/hooks";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { Store } from "components/Sidebar/StoreSearch/StoreList";
import { OLModal } from "components/Tooltips/OLModal";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { setCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { ListResponse } from "types/listing";
import { useStyles } from "./TargetTenantModal.styles";

interface Props {
  isOpen: boolean;
  headerText: string;
  setJsonAction: (payload: any) => PayloadAction<any>;
  onClose: () => void;
  data: any;
  link: string;
  buttonText: string;
  infoText: string;
}

export const TargetTenantModal = ({
  isOpen,
  headerText,
  setJsonAction,
  onClose,
  data,
  link,
  infoText,
  buttonText,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { useStoreList } = useStores();

  const [searchedQuery, setSearchedQuery] = useState("");

  const { data: storesList } = useStoreList({
    filters: [
      ...(searchedQuery
        ? [
            {
              condition: "like",
              query: "name",
              value: searchedQuery,
            },
          ]
        : []),
      { condition: "", query: "active", value: true },
    ],
    order: "asc",
    sortKey: "name",
  }) as { data: ListResponse };

  return (
    <OLModal
      open={isOpen}
      header={headerText}
      onClose={onClose}
      modalWidth={536}
      allowOverflow
    >
      <>
        <Formik
          initialValues={{ tenant: undefined }}
          onSubmit={async (values) => {
            const selectedTenant = storesList?.items?.find(
              (item: Store) => item.code === values.tenant,
            ) as Store;
            dispatch(setJsonAction(data));
            dispatch(
              setCurrentStoreData({
                code: selectedTenant.code,
                currency: selectedTenant.currency,
                name: selectedTenant.name,
                id: selectedTenant.storeId,
              }),
            );
            return navigate(link);
          }}
        >
          {({ handleSubmit, isSubmitting, values }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <Box className={classes.infoBox}>
                  <InfoCircleIcon className={classes.icon} />
                  <Typography className={classes.infoBoxCopy}>
                    {message({ id: "targetTenantTransferInfo" })}
                  </Typography>
                </Box>
                <SelectInput
                  displaySearch
                  name="tenant"
                  items={storesList?.items?.map((item) => ({
                    id: item.code,
                    label: item.name,
                  }))}
                  placeholder={message({ id: "targetTenant" })}
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "targetTenant" })}
                    />
                  }
                  asyncSearch={(query) => setSearchedQuery(query)}
                  data-test-id="target-tenant"
                />
                <Typography className={classes.copy}>{infoText}</Typography>
                <OLButton
                  color="primary"
                  disabled={isSubmitting || !values.tenant || !data}
                  fullWidth
                  type="submit"
                  variant="contained"
                  data-test-id="confirm-duplicate"
                >
                  {buttonText}
                </OLButton>
              </Form>
            );
          }}
        </Formik>
      </>
    </OLModal>
  );
};
